/**
 * usePlan — feature-gating hook for QENVA Insight / Guardian / Sentinel.
 *
 * Reads the active workspace's `plan` field (exposed by the backend on
 * GET /api/workspaces and GET /api/workspaces/{id}) and provides:
 *   - plan: "insight" | "guardian" | "sentinel"
 *   - isInsight / isGuardian / isSentinel: booleans
 *   - canAccess(featureKey): boolean
 *   - minPlanFor(featureKey): the lowest plan that unlocks the feature
 *   - rankOf(plan): numeric rank (insight=0, guardian=1, sentinel=2)
 *
 * Single source of truth for the feature → plan mapping lives here
 * (FEATURE_PLAN_MAP). The AppLayout menu and UpgradeModal both read from
 * this table — no string duplication anywhere else.
 *
 * Why a static table rather than a backend endpoint?
 *   - Feature gating is enforced server-side too (workspaces.assert_plan);
 *     this hook drives display only.
 *   - The set of plans + features is small and rarely changes.
 *   - Keeps the hook synchronous (no async waterfall on every page).
 */
import { useWorkspace } from "./WorkspaceContext";
import { useAuth } from "./AuthContext";

export const PLAN_INSIGHT = "insight";
export const PLAN_GUARDIAN = "guardian";
export const PLAN_SENTINEL = "sentinel";

export const PLAN_RANK = {
  [PLAN_INSIGHT]: 0,
  [PLAN_GUARDIAN]: 1,
  [PLAN_SENTINEL]: 2,
};

// Feature → minimum plan required to access it.
//
// To add a new gated feature: add a key here, then call canAccess("...") at
// the gating point. Do NOT hardcode the plan list anywhere else.
export const FEATURE_PLAN_MAP = {
  // Universal — all plans
  dashboard_basic:     PLAN_INSIGHT,
  documents:           PLAN_INSIGHT,
  alerts:              PLAN_INSIGHT,
  under_watch:         PLAN_INSIGHT,
  invoices_summary:    PLAN_INSIGHT,
  upload:              PLAN_INSIGHT,
  // Guardian + Sentinel
  daily_brief:         PLAN_GUARDIAN,
  under_watch_full:    PLAN_GUARDIAN,
  guardian_score:      PLAN_GUARDIAN,
  cashflow_forecast:   PLAN_GUARDIAN,
  rapports_vigilance:  PLAN_GUARDIAN,
  slack_notifications: PLAN_GUARDIAN,
  // Sentinel only
  pre_signature:       PLAN_SENTINEL,
  crosscheck:          PLAN_SENTINEL,
  lineage:             PLAN_SENTINEL,
  executive_report:    PLAN_SENTINEL,
  alm_collaborative:   PLAN_SENTINEL,
  export_csv:          PLAN_SENTINEL,
  multi_users:         PLAN_SENTINEL,
};

const DEFAULT_PLAN = PLAN_GUARDIAN;

export function rankOf(plan) {
  return PLAN_RANK[plan] ?? PLAN_RANK[DEFAULT_PLAN];
}

export function minPlanFor(feature) {
  return FEATURE_PLAN_MAP[feature] || PLAN_INSIGHT;
}

export function canAccessWithPlan(plan, feature) {
  const required = minPlanFor(feature);
  return rankOf(plan) >= rankOf(required);
}

export function usePlan() {
  const { current } = useWorkspace();
  const { user } = useAuth();
  const plan = current?.plan || DEFAULT_PLAN;
  // Iter69.7 — platform-superusers (`user.role === "admin"`, distinct from the
  // per-workspace admin role) bypass every plan gate. We DO NOT falsify the
  // displayed plan — the UI still shows the workspace's real tier — we just
  // short-circuit `canAccess()` so their sidebar shows no locks and their
  // navigation is unrestricted.
  const isSuperuser = user?.role === "admin";
  return {
    plan,
    isInsight: plan === PLAN_INSIGHT,
    isGuardian: plan === PLAN_GUARDIAN,
    isSentinel: plan === PLAN_SENTINEL,
    isSuperuser,
    canAccess: (feature) => isSuperuser || canAccessWithPlan(plan, feature),
    minPlanFor,
    rankOf,
  };
}
